// Shared helpers for the nightly eval drill-down panel.
//
// Keeping the export + classify + redact logic in one module has two purposes:
//   1) The admin panel and the vitest sanity check import the exact same
//      code path — the test can guarantee the on-screen table and the
//      downloaded CSV/JSON always agree.
//   2) PII redaction lives in one place so we can prove (via test) that
//      exported files never leak emails/UUIDs/long digit runs that the
//      renderer would have hidden.

export type EvalFailure = {
  id: string;
  intent: string;
  reason: string;
  violations: string[];
  answer_preview: string;
  category?: string;
};

export type FailureCategory = "accuracy" | "injection" | "citation" | "rls" | "other";

// Match order in the edge function classifier so admin drill-down and
// alert routing bucket the same failure identically.
export function classifyFailure(f: EvalFailure): FailureCategory {
  if (f.category) return f.category as FailureCategory;
  const v = (f.violations ?? []).join(",").toLowerCase();
  const r = (f.reason ?? "").toLowerCase();
  if (v.includes("injection") || r.includes("injection") || r.includes("system prompt")) return "injection";
  if (v.includes("citation") || r.includes("citation") || r.includes("uncited")) return "citation";
  if (v.includes("rls") || r.includes("denied") || r.includes("permission")) return "rls";
  if (v.includes("accuracy") || r.includes("expected") || r.includes("missing")) return "accuracy";
  return "other";
}

// PII patterns — kept in sync with supabase/functions/_shared/helpdesk-safety.ts
// and the edge-function redactor in helpdesk-eval-record.
export const PII_PATTERNS = {
  email: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi,
  uuid: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
  longDigits: /\b\d{9,}\b/g,
};

export function redactPII(s: string | null | undefined): string {
  return (s ?? "")
    .replace(PII_PATTERNS.email, "[redacted-email]")
    .replace(PII_PATTERNS.uuid, "[redacted-id]")
    .replace(PII_PATTERNS.longDigits, "[redacted-number]");
}

// Detect any residual PII in a rendered/exported blob. Returns the list of
// offending patterns so callers can log/report them (test uses this as its
// assertion; the panel uses it defensively before a download).
export function detectPII(text: string): string[] {
  const hits: string[] = [];
  if (PII_PATTERNS.email.test(text)) hits.push("email");
  PII_PATTERNS.email.lastIndex = 0;
  if (PII_PATTERNS.uuid.test(text)) hits.push("uuid");
  PII_PATTERNS.uuid.lastIndex = 0;
  if (PII_PATTERNS.longDigits.test(text)) hits.push("longDigits");
  PII_PATTERNS.longDigits.lastIndex = 0;
  return hits;
}

export function csvEscape(v: unknown): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export type ExportRun = {
  id: string;
  created_at: string;
  suite: string;
  git_sha: string | null;
  branch: string | null;
  total: number;
  passed: number;
  failed: number;
  accuracy: number;
  baseline_accuracy: number;
  citation_pass_rate: number | null;
  injection_pass_rate: number | null;
  by_intent: Record<string, { passed: number; total: number }>;
};

export type ExportFilters = { category: string; intent: string; search: string };

export function filterFailures(
  failures: EvalFailure[],
  filters: ExportFilters,
): EvalFailure[] {
  const search = filters.search.toLowerCase();
  return failures.filter((f) => {
    if (filters.category !== "all" && classifyFailure(f) !== filters.category) return false;
    if (filters.intent !== "all" && f.intent !== filters.intent) return false;
    if (search && !`${f.id} ${f.reason} ${f.answer_preview}`.toLowerCase().includes(search)) return false;
    return true;
  });
}

const CSV_HEADERS = ["id", "intent", "category", "reason", "violations", "answer_preview"] as const;

export function buildCsv(failures: EvalFailure[]): string {
  const rows = failures.map((f) => {
    const cat = classifyFailure(f);
    const cells: string[] = CSV_HEADERS.map((h) => {
      if (h === "category") return csvEscape(cat);
      if (h === "violations") return csvEscape((f.violations ?? []).join("|"));
      // Belt-and-braces: also redact at export time in case renderer bypass.
      return csvEscape(redactPII(String((f as Record<string, unknown>)[h] ?? "")));
    });
    return cells.join(",");
  });
  return [CSV_HEADERS.join(","), ...rows].join("\n");
}

export function buildJson(
  run: ExportRun,
  failures: EvalFailure[],
  filters: ExportFilters,
): string {
  const cleaned = failures.map((f) => ({
    id: f.id,
    intent: f.intent,
    category: classifyFailure(f),
    reason: redactPII(f.reason),
    violations: f.violations ?? [],
    answer_preview: redactPII(f.answer_preview),
  }));
  return JSON.stringify(
    {
      run: {
        id: run.id,
        created_at: run.created_at,
        suite: run.suite,
        git_sha: run.git_sha,
        branch: run.branch,
        total: run.total,
        passed: run.passed,
        failed: run.failed,
        accuracy: run.accuracy,
        baseline_accuracy: run.baseline_accuracy,
        citation_pass_rate: run.citation_pass_rate,
        injection_pass_rate: run.injection_pass_rate,
        by_intent: run.by_intent,
      },
      filters,
      failures: cleaned,
    },
    null,
    2,
  );
}
